import React from 'react';
import SidebarParametres from '../../../layout/SidebarParametres';
import { useOAD } from '../../../layout/OADLayout';
import { api } from '../../shared/api/api';
import { useApiData } from '../../shared/hooks/useFetch';
import LimiteErreur from '../../shared/components/LimiteErreur';
import ParametresEau3Mois, { useParametresPourApi } from '../../shared/components/ParametresEau3Mois';
import { SousTitreSysteme, NotesSysteme } from '../../shared/components/Eau3MoisTextes';
import Eau3MoisDistributionChart from '../../shared/charts/Eau3MoisDistributionChart';
import {
  libelleGrandeur, ajouterJours, bornesDistribution, resume,
  formatLong, formatCourt, formatJJMM, formatJJMMAAAA, formatNombre,
} from '../../shared/utils/eau3mois';

// Le curseur s'ouvre sur le 1er juillet (ou sur la première date de la fenêtre si
// la coupure choisie est plus tardive).
const DATE_PAR_DEFAUT = '2026-07-01';

/**
 * Eau à 3 mois / Distribution — port de l'onglet "Distribution" du Shiny :
 * distribution du cumul de toutes les trajectoires à la date choisie au
 * curseur (de la coupure au 15 octobre), échelles X et Y figées.
 */
export default function Distribution() {
  const { idExploit } = useOAD();
  const { pret, parametres } = useParametresPourApi({ grandeurDefaut: 'culture' });
  const { data, loading, error } = useApiData(api.getEau3MoisDistribution, parametres, { skip: !pret });

  const [dateChoisie, setDateChoisie] = React.useState(DATE_PAR_DEFAUT);
  const [lecture, setLecture] = React.useState(false);

  const dates = React.useMemo(
    () => (data ? data.valeurs.map((_, i) => ajouterJours(data.origine, data.j0 + i)) : []),
    [data]
  );
  // Échelles figées : calculées UNE fois par scénario, pas à chaque date.
  const bornes = React.useMemo(() => (data ? bornesDistribution(data.valeurs) : null), [data]);

  // Date hors fenêtre (changement de coupure) : on la ramène dans la fenêtre.
  const index = dates.length
    ? Math.min(Math.max(dates.indexOf(dateChoisie) >= 0 ? dates.indexOf(dateChoisie)
      : dateChoisie < dates[0] ? 0 : dates.length - 1, 0), dates.length - 1)
    : 0;

  // Lecture automatique du curseur (animation du Shiny, en boucle).
  React.useEffect(() => {
    if (!lecture || !dates.length) return undefined;
    const t = setInterval(() => {
      setDateChoisie((courante) => {
        const i = dates.indexOf(courante);
        return dates[i < 0 || i >= dates.length - 1 ? 0 : i + 1];
      });
    }, 350);
    return () => clearInterval(t);
  }, [lecture, dates]);

  const valeurs = data ? data.valeurs[index] : null;
  const r = valeurs ? resume(valeurs) : null;

  return (
    <div className="oad-contenu">
      <SidebarParametres niveau1="annee-en-cours" niveau2="eau-3-mois" niveau3="distribution" idExploit={idExploit}>
        <ParametresEau3Mois avecProduction grandeurDefaut="culture" />
        {dates.length > 0 && (
          <div className="sidebar-champ" style={{ gap: 2 }}>
            <span className="sidebar-label" style={{ marginBottom: 0 }}>
              Date de distribution : <b>{formatJJMM(dates[index])}</b>
            </span>
            <input
              type="range" min={0} max={dates.length - 1} step={1} value={index}
              onChange={(e) => setDateChoisie(dates[Number(e.target.value)])}
              style={{ width: '100%', margin: '2px 0' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
              <span>{formatJJMM(dates[0])}</span>
              <button type="button" onClick={() => setLecture((l) => !l)} aria-pressed={lecture}
                style={{ padding: '1px 8px' }}>
                {lecture ? '⏸ Pause' : '▶ Lecture'}
              </button>
              <span>{formatJJMM(dates[dates.length - 1])}</span>
            </div>
          </div>
        )}
      </SidebarParametres>

      <main className="oad-cards">
        <section className="oad-card">
          <h2 className="tdb-section-titre">
            {data && `Distribution du cumul au ${formatLong(dates[index])} — `}
            {libelleGrandeur(parametres.type, parametres.grandeur)}
          </h2>
          {error && <p className="tdb-erreur">{error}</p>}
          {loading && <p className="tdb-chargement">Chargement…</p>}
          {data && valeurs && bornes && (
            <>
              <SousTitreSysteme info={data.info} coupure={data.coupure} rcp={parametres.rcp} />
              <LimiteErreur cle={`distrib-${JSON.stringify(parametres)}`}>
                <Eau3MoisDistributionChart valeurs={valeurs} bornes={bornes} />
              </LimiteErreur>
              <p style={{ fontSize: 13, color: '#555' }}>
                Échelles X et Y communes à toutes les dates du {formatCourt(dates[0])} au {formatCourt(dates[dates.length - 1])}.
              </p>

              <p className="ig-tableau-sous-titre" style={{ marginTop: 12 }}>Cumul en millions de m³</p>
              <table className="oad-data-table oad-table-centered">
                <thead>
                  <tr><th>Date</th><th>Min</th><th>Q25</th><th>Médiane</th><th>Moyenne</th><th>Q75</th><th>Max</th><th>N trajectoires</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{formatJJMMAAAA(dates[index])}</td>
                    {[r.min, r.q25, r.med, r.moy, r.q75, r.max].map((v, i) => <td key={i}>{formatNombre(v, 2)}</td>)}
                    <td>{r.n}</td>
                  </tr>
                </tbody>
              </table>
              <NotesSysteme info={data.info} grandeur={parametres.grandeur} />
            </>
          )}
        </section>
      </main>
    </div>
  );
}
